import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { svgResize } from 'blockly/core';
import BlocklyContainer from '@modules/blocklyNew/components/blockly';
import MLPanel from '@modules/blockly/components/MLPanel';
import CodePanel from '@modules/blockly/components/codePanel';
import ToolsPanel from '@modules/blockly/components/toolsPanel';
import FooterPanel from '@modules/blockly/components/footerPanel';
import CompilationProgress from '@modules/blockly/components/compilationProgress';
import boardTypes from '@modules/boards/features/constants';
import { setMLActive } from '@modules/blockly/features/blocklySlice';
import useCreabotsTheme from '@sections/creabots/hooks/useCreabotsTheme';
import { getCosmosItem } from '../../../cosmos';
import { useBlockly } from '../features/hooks';
import { BoardVersionChecker } from '../features/utils/boardVersionChecker';
import '../components/codePanel.scss';


// Constante de módulo: el tema del cosmos no cambia en runtime. Aula y labs son claros.
const themeKey = getCosmosItem('theme');
const darkThemeByDefault = !(themeKey === 'theme-aula' || themeKey === 'theme-labs');

const Blockly = React.memo((props) => {
  const [blocklyWorkspace, setBlocklyWorkspace] = useState(null);
  const [codeToggle, setCodeToggle] = useState(false);
  const [darkthemeToggle, setDarkthemeToggle] = useState(props.darktheme ?? darkThemeByDefault);
  const [toolboxToggle, setToolboxToggle] = useState(false);
  const [selectedPort, setSelectedPort] = useState(props.tabInfo?.port || '');
  const dispatch = useDispatch();
  const blocklyState = useBlockly();
  const agentState = useSelector((state) => state.bloquesReducer);
  const isCreabots = props.isCreabots || false;
  const { isDarkMode: creabotsIsDarkMode } = useCreabotsTheme();
  const iaModel = props.tabInfo?.board?.iaModel || null;
  // En colaboración el canvas va en un wrapper posicionado propio (ver render): sin él el ToolsPanel
  // se posiciona contra el `.content` de la ruta y queda dentro del canvas.
  const isCollaboration = !!(props.tabInfo?.collaborationProject && props.tabInfo?.collaborationSession);

  // Sin gate de isCreabots: con iaModel el panel ML abre directo en modo IA. Creabots además despacha
  // su propio setMLActive desde userProject.jsx.
  useEffect(() => {
    dispatch(setMLActive(iaModel !== null));
  }, [iaModel]);

  // En colaboración el Header y el RightPanel son hermanos de este container y no ven el toggle de
  // tema: se les expone por callback. Solo colaboración pasa onThemeChange.
  useEffect(() => {
    if (props.onThemeChange) {
      props.onThemeChange(darkthemeToggle);
    }
  }, [darkthemeToggle]);

  // En Creabots el modo claro/oscuro lo elige el Header, fuera de este árbol, con la clase
  // `theme-labs-dark` en body; useCreabotsTheme es la fuente de verdad.
  useEffect(() => {
    if (isCreabots) {
      setDarkthemeToggle(creabotsIsDarkMode);
    }
  }, [isCreabots, creabotsIsDarkMode]);
  // El código lo deja components/blockly.jsx en el slice en cada cambio del workspace.
  const codeGenerated = blocklyState.project?.code || '';
  const showCodePanel = (props.tabInfo?.board?.type !== boardTypes.CODE) && !props.noCodePanel;

  // console.log('=========blocklyState', agentState, blocklyState);
  const boardVersionChecker = new BoardVersionChecker(blocklyWorkspace, agentState.agentPorts);
  // agentPorts va en las deps: la lista llega después de conectar y sin re-detectar la versión de
  // placa queda la vieja.
  useEffect(() => {
    boardVersionChecker.setPort(blocklyState.build?.port || '');
  }, [blocklyState.build?.port, blocklyWorkspace, agentState.agentPorts]);

  const updateWorkspace = (workspace) => {
    if (workspace) {
      setBlocklyWorkspace(workspace);
      if (props.setWorkspace) {
        props.setWorkspace(workspace);
      }
    }
  }

  // Los paneles le comen ancho al canvas: hay que recalcular el SVG después de la transición.
  const resizeAfterPanelTransition = () => {
    setTimeout(() => {
      if (blocklyWorkspace) {
        svgResize(blocklyWorkspace);
      }
    }, 100);
  };

  const codeToggleAction = () => {
    setCodeToggle(!codeToggle);
    resizeAfterPanelTransition();
  };

  const mlPanelToggleAction = resizeAfterPanelTransition;

  const buildBlockly = (params) => {
    if (props.buildingFunction) {
      props.buildingFunction(params);
    }
  };

  const onPortChange = (newPort) => {
    setSelectedPort(newPort);
  };

  // zoomCenter y no setScale: setScale reescala sobre el origen del workspace y corre los bloques
  // hacia el toolbox en cada click.
  const setZoom = (direction) => {
    if (!blocklyWorkspace) return;
    blocklyWorkspace.zoomCenter(direction === 'up' ? 1 : -1);
  };

  const setCenter = () => {
    if (!blocklyWorkspace) return;
    const { startScale } = blocklyWorkspace.options.zoomOptions;
    blocklyWorkspace.setScale(startScale);
    setTimeout(() => { blocklyWorkspace.scrollCenter(); }, 100);
  };

  const setUndo = (direction) => {
    if (!blocklyWorkspace) return;
    blocklyWorkspace.undo(direction);
  };

  const toggleDarkTheme = () => {
    setDarkthemeToggle(!darkthemeToggle);
  };

  // El modo ícono del toolbox no tiene estilos en v13: el swap de clase evita dejar el botón muerto,
  // pero el toolbox no se compacta.
  const changeToolboxToggle = () => {
    const root = blocklyWorkspace?.getToolbox?.()?.HtmlDiv;
    if (root) {
      root.classList.toggle('blocklyToolboxDiv-text', toolboxToggle);
      root.classList.toggle('blocklyToolboxDiv-icons', !toolboxToggle);
    }
    setToolboxToggle(!toolboxToggle);
  };

  const blocklyCanvas = (
    <BlocklyContainer
      userData={props.userData || {}}
      tabInfo={props.tabInfo || {}}
      socket={props.socket}
      teamList={props.teamList}
      setWorkspace={updateWorkspace}
      darkTheme={darkthemeToggle}
    />
  );

  return (
    <>
      {isCollaboration ? (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          {blocklyCanvas}
        </div>
      ) : blocklyCanvas}

      {(props.tabInfo?.board?.type !== boardTypes.CODE) ? (
        <ToolsPanel
          onSetCenter={setCenter}
          onSetUndo={setUndo}
          onSetZoom={setZoom}
          onChangeToolboxToggle={changeToolboxToggle}
          onToggleDarkTheme={toggleDarkTheme}
          toolboxToggle={toolboxToggle}
          darkthemeToggle={darkthemeToggle}
          isCreabots={isCreabots}
        />
      ) : ('')}

      {showCodePanel ? (
        <div className="blocklyNewCodePanel">
          <CodePanel
            active={codeToggle}
            content={codeGenerated}
            onChange={codeToggleAction}
            darktheme={darkthemeToggle}
            id={`blockly-instance-${props.tabInfo?.id || 'id'}`}
          />
        </div>
      ) : ('')}

      {/* Se gatea el montaje entero por iaModel, a diferencia del legacy, que mostraba el botón de IA
          con un panel vacío en perfiles sin modelo. También evita instanciar useMachineLearning,
          useBluetooth y react-vis fuera de ML. */}
      {(!isCreabots && iaModel) ? (
        <MLPanel
          onChange={mlPanelToggleAction}
          workspace={blocklyWorkspace}
          iaModel={iaModel}
          iaModelJson={props.iaModelJson || null}
          iaModelBin={props.iaModelBin || null}
          onFinishLoading={props.onFinishLoading}
          board={props.tabInfo?.board || {}}
          darktheme={darkthemeToggle}
          isCreabots={isCreabots}
          isFreeProject={props.isFreeProject || false}
        />
      ) : ('')}

      {/* Solo colaboración pasa buildAllowed=true (OWNER); en ML/Creabots no se monta. */}
      {(props.tabInfo?.active && props.buildAllowed) ? (
        <FooterPanel
          id={`blockly-instance-${props.tabInfo?.id || 'id'}`}
          socketAgent={props.socketAgent}
          socketAgentType={props.socketAgentType}
          board={props.tabInfo?.board}
          port={selectedPort}
          boardType={props.tabInfo?.board?.type}
          toolboxToggle={toolboxToggle}
          horizontalLayout={props.tabInfo?.board?.toolboxLayout === 'horizontal'}
          buildingFunction={buildBlockly}
          selectPortChange={onPortChange}
          portChangeAllowed={props.portChangeAllowed}
        />
      ) : ('')}

      {(props.buildAllowed) ? (
        <CompilationProgress
          id={`blockly-instance-${props.tabInfo?.id || 'id'}`}
          progress={props.buildProgress || 0}
          microbitMode={(props.tabInfo?.className === 'microbit')}
          userName={props.userData?.name || ''}
        />
      ) : ('')}

      {/* Clases globales de App.scss; blocklyState.loading lo despacha el componente solo en colaboración. */}
      {(isCollaboration && blocklyState.loading) ? (
        <div className="show loaderOverAll">
          <div className="anibot-large-white" />
        </div>
      ) : ('')}
    </>
  );
});

export default Blockly;
